import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseHttpService } from '../http/base.http.service';
import { ApiRouterService } from '../http/api.router';
import { objectToSnake } from 'ts-case-convert';
import { SuggestionSortType } from 'src/app/models/enums/suggestionSortType';
import { IBase } from 'src/app/models/base';
import { ISuggestion } from 'src/app/models/suggestion';
import { CommunicationUserType, SuggestionPriority, SuggestionStatus, SuggestionType } from 'src/app/models/enums';

function entityReference(entity?: IBase | null): IBase | null {
  return entity?.id ? { id: entity.id } : null;
}

@Injectable({
  providedIn: 'root',
})
export class SuggestionHttpService extends BaseHttpService {
  private baseURL = `suggestions`;

  constructor(
    @Inject(HttpClient) httpClient: HttpClient,
    private routerService: ApiRouterService,
  ) {
    super(httpClient);
  }

  public createSuggestion(suggestion: ISuggestion) {
    const url = this.routerService.mount(`${this.baseURL}`);
    return this.create(this.buildSuggestionBody(suggestion), url);
  }

  public updateSuggestion(suggestion: ISuggestion) {
    const url = this.routerService.mount(`${this.baseURL}/${suggestion.id}`);
    return this.update(this.buildSuggestionBody(suggestion), url);
  }

  private buildSuggestionBody(suggestion: ISuggestion) {
    return objectToSnake({
      type: suggestion.type,
      affair: suggestion.affair,
      description: suggestion.description,
      comments: suggestion.comments,
      priority: suggestion.priority,
      status: suggestion.status,
      town: entityReference(suggestion.town),
      client: entityReference(suggestion.client),
      service: entityReference(suggestion.service),
      userSuggester: entityReference(suggestion.userSuggester),
    });
  }

  public getSuggestions(
    userType: CommunicationUserType,
    page: number = 1,
    size: number = 10,
    sort: SuggestionSortType = SuggestionSortType.CREATION,
  ) {
    let path = `?page=${page}&size=${size}&sorting=${sort}`;
    const url = this.routerService.mount(`${this.baseURL}/${userType}${path}`);
    return this.read(url);
  }

  public getSuggestionById(id: number) {
    const url = this.routerService.mount(`${this.baseURL}/${id}`);
    return this.read(url);
  }

  public filter(
    communicationUserType: CommunicationUserType,
    page: number = 1,
    size: number = 10,
    sort: SuggestionSortType = SuggestionSortType.CREATION,
    clientsSelected: number[],
    townsSelected: number[],
    typesSelected: SuggestionType[],
    statusSelected: SuggestionStatus[],
    prioritiesSelected: SuggestionPriority[],
    searchValue?: string,
    servicesSelected?: number[],
  ) {
    let path = `/filter?page=${page}&size=${size}&sorting=${sort}`;

    if (clientsSelected && clientsSelected.length > 0) {
      const clients = clientsSelected.join(',');
      path += `&clients=${clients}`;
    }
    if (townsSelected && townsSelected.length > 0) {
      const towns = townsSelected.join(',');
      path += `&towns=${towns}`;
    }
    if (servicesSelected && servicesSelected.length > 0) {
      const services = servicesSelected.join(',');
      path += `&services=${services}`;
    }
    if (prioritiesSelected && prioritiesSelected.length > 0) {
      const priorities = prioritiesSelected.map((priority: any) => priority).join(',');
      path += `&priorities=${priorities}`;
    }
    if (typesSelected && typesSelected.length > 0) {
      const types = typesSelected.map((type: any) => type).join(',');
      path += `&types=${types}`;
    }
    if (statusSelected && statusSelected.length > 0) {
      const status = statusSelected.map((status: any) => status).join(',');
      path += `&status=${status}`;
    }

    path += searchValue ? `&search=${encodeURIComponent(searchValue)}` : '';
    const url = this.routerService.mount(`${this.baseURL}/${communicationUserType}${path}`);
    return this.read(url);
  }

  public sendConversationMessage(suggestionId: number, newMessage: string) {
    const url = this.routerService.mount(`${this.baseURL}/${suggestionId}/comments`);
    return this.create(newMessage, url);
  }

  public getPendingSuggestionsCount() {
    const url = this.routerService.mount(`${this.baseURL}/pending`);
    return this.read(url);
  }
}
